import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const STORAGE_PREFIX = 'scroll:';

/**
 * Paginageheugen voor scrollposities, per URL (pad + querystring, dus ook per
 * ?tab=). Bewaart doorlopend in sessionStorage zodat naast terug/vooruit ook
 * een refresh (F5) en een tab-wissel de positie terugvinden.
 *
 * - POP (terug/vooruit) en eerste mount (reload/directe link): herstellen
 * - PUSH (nieuwe navigatie): naar boven — behalve een tab-/subtabwissel
 *   (state.zelfScrollen, zie useZetZoekParams): die krijgt de bewaarde positie
 *   van die tab terug, of begint bovenaan
 * - REPLACE: niets — de tab-schermen roepen zo nodig zelf restoreScroll() aan
 *
 * Herstellen gebeurt met een retry-lus: pagina's laden hun data async, dus
 * direct scrollen klemt vast op de hoogte van het skeleton. We proberen tot de
 * pagina hoog genoeg is, de gebruiker zelf scrolt, of de tijd om is.
 */

const storageKey = () => STORAGE_PREFIX + window.location.pathname + window.location.search;

let restoreToken = 0;

export function saveScrollNow() {
  try {
    sessionStorage.setItem(storageKey(), String(Math.round(window.scrollY)));
  } catch {
    /* opslag vol of geblokkeerd — geen ramp */
  }
}

/** Annuleert een eventueel lopende herstel-lus (bij nieuwe navigatie). */
export function cancelScrollRestore() {
  restoreToken++;
}

export function restoreScroll(opts?: { fallbackTop?: boolean; maxMs?: number }) {
  const maxMs = opts?.maxMs ?? 2500;
  let saved: string | null = null;
  try {
    saved = sessionStorage.getItem(storageKey());
  } catch {
    return;
  }
  const y = saved === null ? NaN : parseInt(saved, 10);
  if (!Number.isFinite(y) || y <= 0) {
    // Geen (bruikbare) positie bekend voor deze URL: bij tab-wissels bovenaan
    // beginnen in plaats van de scroll van de vorige tab te erven.
    if (opts?.fallbackTop) {
      cancelScrollRestore();
      window.scrollTo(0, 0);
    }
    return;
  }

  const token = ++restoreToken;
  const start = performance.now();
  let userScrolled = false;
  const onUser = () => {
    userScrolled = true;
  };
  window.addEventListener('wheel', onUser, { passive: true });
  window.addEventListener('touchmove', onUser, { passive: true });
  const cleanup = () => {
    window.removeEventListener('wheel', onUser);
    window.removeEventListener('touchmove', onUser);
  };

  const attempt = () => {
    if (token !== restoreToken || userScrolled) {
      cleanup();
      return;
    }
    const maxY = document.documentElement.scrollHeight - window.innerHeight;
    if (maxY >= y) {
      window.scrollTo(0, y);
      cleanup();
      return;
    }
    if (performance.now() - start > maxMs) {
      window.scrollTo(0, Math.max(0, maxY));
      cleanup();
      return;
    }
    // Alvast zo ver mogelijk mee-scrollen terwijl de data binnenkomt.
    window.scrollTo(0, Math.max(0, maxY));
    requestAnimationFrame(attempt);
  };
  requestAnimationFrame(attempt);
}

export function useScrollRestoration() {
  const location = useLocation();
  const navType = useNavigationType();
  const didMountRef = useRef(false);

  // Doorlopend opslaan: throttled bij scrollen, en vlak vóór een klik of het
  // verlaten van de pagina. De sleutel leest live window.location, dus ook
  // tab-wissels via replaceState krijgen hun eigen positie.
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    let timer: number | null = null;
    const onScroll = () => {
      if (timer !== null) return;
      timer = window.setTimeout(() => {
        timer = null;
        saveScrollNow();
      }, 120);
    };
    const onClickCapture = () => saveScrollNow();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pagehide', saveScrollNow);
    document.addEventListener('click', onClickCapture, true);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pagehide', saveScrollNow);
      document.removeEventListener('click', onClickCapture, true);
      if (timer !== null) window.clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (!didMountRef.current) {
      // Eerste mount = reload of directe link: positie van deze URL terugzetten.
      didMountRef.current = true;
      restoreScroll();
      return;
    }
    if (navType === 'POP') {
      restoreScroll();
    } else if (navType === 'PUSH') {
      const state = location.state as { zelfScrollen?: boolean } | null;
      if (state?.zelfScrollen) {
        restoreScroll({ fallbackTop: true });
        return;
      }
      cancelScrollRestore();
      window.scrollTo(0, 0);
    }
    // location.state hoort bij location.key; die is de echte trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, navType]);
}
